import {
  AGENT_RUN_STATUS_LABELS,
  AGENT_RUN_TRIGGER_LABELS,
  formatDateTime,
  formatNumber,
  formatRelative,
  type AgentDto,
  type AgentKey,
  type AgentRunDto,
  type SignalDto,
} from '@nova/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CalendarClock,
  ChevronDown,
  ExternalLink,
  Info,
  Play,
  Radar,
  Terminal,
} from 'lucide-react';
import { useState } from 'react';
import { useAuth } from '../auth/AuthProvider.js';
import { Button } from '../components/Button.js';
import { EmptyState } from '../components/EmptyState.js';
import { GlassPanel, PanelHeader } from '../components/GlassPanel.js';
import { NovaOrb } from '../components/NovaOrb.js';
import { SkeletonPanel } from '../components/Skeleton.js';
import { AgentSettings } from '../features/agents/AgentSettings.js';
import { api } from '../lib/api.js';
import { useAreaColor } from '../lib/area-color.js';
import { cn } from '../lib/cn.js';

/**
 * Per-agent guidance on when a manual run is actually worth it. The agents run
 * on their own schedule, so the recurring message is: a manual run is a nudge
 * for when you need something right now, not an everyday action.
 */
const MANUAL_RUN_HINTS: Record<AgentKey, string> = {
  director:
    'Koordinerer de andre agentene automatisk. Kjør manuelt bare hvis du vil ha en fersk gjennomgang med en gang.',
  planner:
    'Vanligvis unødvendig — kjører automatisk. Kjør manuelt bare hvis du vil ha friske forslag nå.',
  scout:
    'Går hver morgen av seg selv. Kjør manuelt bare hvis du venter på ferske signaler etter en nyhet.',
  writer:
    'Trengs sjelden manuelt — innhold skrives der du står på kortet. Kjør bare hvis en hel runde skal friskes opp.',
  reminder: 'Går av seg selv; manuell kjøring er sjelden nødvendig.',
  analyst:
    'Henter KPI-er på plan. Kjør manuelt bare hvis du vil oppdatere tallene før en rapport.',
  digest: 'Går av seg selv; manuell kjøring er sjelden nødvendig.',
  'tender-scout':
    'Skanner anbud automatisk. Kjør manuelt bare hvis du venter på et ferskt anbud akkurat nå.',
};

export function AgentsPage() {
  const { user } = useAuth();
  const areaColor = useAreaColor();
  const queryClient = useQueryClient();
  const [expanded, setExpanded] = useState<string | null>(null);

  const { data: agents = [], isLoading } = useQuery({
    queryKey: ['agents'],
    queryFn: () => api.get<AgentDto[]>('/agents'),
    refetchInterval: 10_000,
  });

  const { data: signals = [] } = useQuery({
    queryKey: ['signals'],
    queryFn: () => api.get<SignalDto[]>('/signals?take=12'),
  });

  const toggle = useMutation({
    mutationFn: ({ key, isEnabled }: { key: string; isEnabled: boolean }) =>
      api.patch(`/agents/${key}`, { isEnabled }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['agents'] }),
  });

  const run = useMutation({
    mutationFn: (key: string) => api.post(`/agents/${key}/run`, {}),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['agents'] }),
  });

  const canOperate = user?.role !== 'VIEWER';

  return (
    <div className="space-y-4">
      <header>
        <h1 className="font-display text-ink text-xl">Agenter</h1>
        <p className="text-ink-soft mt-0.5 text-sm">
          Nova består av seks agenter. Ingen av dem publiserer noe uten at et menneske har sagt ja.
        </p>
        <p className="text-ink-soft/80 mt-1.5 flex items-start gap-1.5 text-[13px] leading-relaxed">
          <Info size={14} className="mt-0.5 shrink-0" aria-hidden />
          <span>
            Agentene kjører på egen tidsplan og trenger sjelden en dytt. «Kjør nå» er for når du
            trenger noe med en gang — ikke noe du bruker til daglig.
          </span>
        </p>
      </header>

      {!isLoading && agents.length > 0 && <SchedulePanel agents={agents} />}

      {isLoading ? (
        <div className="grid gap-3 md:grid-cols-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <SkeletonPanel key={i} lines={3} />
          ))}
        </div>
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          {agents.map((agent) => (
            <AgentCard
              key={agent.key}
              agent={agent}
              expanded={expanded === agent.key}
              onToggleExpand={() => setExpanded(expanded === agent.key ? null : agent.key)}
              onRun={canOperate ? () => run.mutate(agent.key) : undefined}
              onToggleEnabled={
                user?.role === 'ADMIN'
                  ? () => toggle.mutate({ key: agent.key, isEnabled: !agent.isEnabled })
                  : undefined
              }
              running={run.isPending && run.variables === agent.key}
            />
          ))}
        </div>
      )}

      <GlassPanel padded={false}>
        <div className="px-5 pt-5">
          <PanelHeader
            title="Signaler fra omverdenen"
            subtitle="Funn fra Omverdensovervåkeren — alltid med kildelenke"
            icon={<Radar size={16} />}
          />
        </div>
        <div className="space-y-2 px-5 pb-5">
          {signals.length === 0 ? (
            <EmptyState title="Ingen signaler ennå. Omverdensovervåkeren kjører hverdager 06:00." />
          ) : (
            signals.map((signal) => (
              <article
                key={signal.id}
                className="border-hairline bg-base/30 rounded-lg border p-3.5"
                style={{ borderLeftColor: areaColor(signal.area?.color), borderLeftWidth: 2 }}
              >
                <div className="flex items-start justify-between gap-3">
                  <p className="text-ink text-sm">{signal.title}</p>
                  <span className="text-ink-soft shrink-0 text-[11px]">
                    {formatRelative(signal.createdAt)}
                  </span>
                </div>
                <p className="text-ink-soft mt-1.5 text-sm leading-relaxed">{signal.summary}</p>
                {signal.suggestedAngle && (
                  <p className="border-violet/20 bg-violet/[0.06] text-ink-soft mt-2 rounded-lg border p-2.5 text-[13px] leading-relaxed">
                    <span className="text-violet">Novas forslag: </span>
                    {signal.suggestedAngle}
                  </p>
                )}
                <div className="mt-2 flex items-center gap-3">
                  <a
                    href={signal.sourceUrl}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="text-accent inline-flex items-center gap-1 text-[11px] hover:underline"
                  >
                    {signal.sourceName ?? 'Kilde'} <ExternalLink size={11} aria-hidden />
                  </a>
                  {signal.area && (
                    <span className="text-ink-soft text-[11px]">{signal.area.name}</span>
                  )}
                  <span className="text-ink-soft text-[11px]">Relevans {signal.relevance}/5</span>
                </div>
              </article>
            ))
          )}
        </div>
      </GlassPanel>
    </div>
  );
}

/** At-a-glance overview of when every agent runs, soonest first. */
function SchedulePanel({ agents }: { agents: AgentDto[] }) {
  const sorted = [...agents].sort((a, b) => {
    if (a.isEnabled !== b.isEnabled) return a.isEnabled ? -1 : 1;
    const ta = a.nextRunAt ? new Date(a.nextRunAt).getTime() : Infinity;
    const tb = b.nextRunAt ? new Date(b.nextRunAt).getTime() : Infinity;
    return ta - tb;
  });

  return (
    <GlassPanel padded={false}>
      <div className="px-5 pt-5">
        <PanelHeader
          title="Kjøreplan"
          subtitle="Når agentene kjører — sortert etter neste kjøring"
          icon={<CalendarClock size={16} />}
        />
      </div>
      <div className="overflow-x-auto px-2 pb-3">
        <table className="w-full min-w-[560px] border-collapse text-sm">
          <thead>
            <tr className="text-left">
              <th className="label-micro px-3 py-2 font-normal">Agent</th>
              <th className="label-micro px-3 py-2 font-normal">Tidsplan</th>
              <th className="label-micro px-3 py-2 font-normal">Neste kjøring</th>
              <th className="label-micro px-3 py-2 font-normal">Sist</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((agent) => {
              const running = Boolean(agent.runningRun);
              const failed = agent.lastRun?.status === 'FAILED';
              return (
                <tr key={agent.key} className="border-hairline border-t">
                  <td className="px-3 py-2.5">
                    <span className="flex items-center gap-2">
                      <span
                        aria-hidden
                        className={cn(
                          'h-2 w-2 shrink-0 rounded-full',
                          running
                            ? 'animate-orb-pulse bg-violet'
                            : !agent.isEnabled
                              ? 'bg-ink-soft/40'
                              : failed
                                ? 'bg-danger'
                                : 'bg-success',
                        )}
                      />
                      <span className="text-ink">{agent.name}</span>
                    </span>
                  </td>
                  <td className="text-ink-soft px-3 py-2.5">{agent.scheduleLabel}</td>
                  <td className="px-3 py-2.5">
                    {!agent.isEnabled ? (
                      <span className="text-ink-soft/70">Slått av</span>
                    ) : running ? (
                      <span className="text-violet">Kjører nå</span>
                    ) : agent.nextRunAt ? (
                      <span className="text-ink">{formatDateTime(agent.nextRunAt)}</span>
                    ) : (
                      <span className="text-ink-soft/70">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2.5">
                    {agent.lastRun ? (
                      <span className={cn('text-[13px]', failed ? 'text-danger' : 'text-ink-soft')}>
                        {AGENT_RUN_STATUS_LABELS[agent.lastRun.status]} ·{' '}
                        {formatRelative(agent.lastRun.startedAt)}
                      </span>
                    ) : (
                      <span className="text-ink-soft/70 text-[13px]">Ikke kjørt</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </GlassPanel>
  );
}

function AgentCard({
  agent,
  expanded,
  onToggleExpand,
  onRun,
  onToggleEnabled,
  running,
}: {
  agent: AgentDto;
  expanded: boolean;
  onToggleExpand: () => void;
  onRun?: () => void;
  onToggleEnabled?: () => void;
  running: boolean;
}) {
  const isRunning = Boolean(agent.runningRun);
  const failed = agent.lastRun?.status === 'FAILED';

  const { data: runs = [] } = useQuery({
    queryKey: ['agent-runs', agent.key],
    queryFn: () => api.get<AgentRunDto[]>(`/agents/${agent.key}/runs?take=15`),
    enabled: expanded,
  });

  return (
    <GlassPanel
      data-testid="agent-card"
      className={cn(isRunning && 'border-violet/40 shadow-glow')}
    >
      <div className="flex items-start gap-3">
        <span className="mt-1">
          <NovaOrb
            status={isRunning ? 'WAITING' : failed ? 'ALERT' : 'OK'}
            size={9}
            animate={isRunning}
          />
        </span>

        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-3">
            <h2 className="font-display text-ink text-base">{agent.name}</h2>
            <span
              className={cn(
                'shrink-0 rounded-full border px-2 py-0.5 text-[11px]',
                !agent.isEnabled
                  ? 'border-ink-soft/25 bg-ink-soft/10 text-ink-soft'
                  : isRunning
                    ? 'border-violet/30 bg-violet/12 text-violet'
                    : 'border-success/25 bg-success/10 text-success',
              )}
            >
              {!agent.isEnabled ? 'Av' : isRunning ? 'Kjører' : 'Aktiv'}
            </span>
          </div>

          <p className="text-ink-soft mt-1.5 text-sm leading-relaxed">{agent.description}</p>

          <dl className="mt-3 grid grid-cols-2 gap-2 text-[11px]">
            <div className="border-hairline bg-base/30 rounded-lg border px-2.5 py-1.5">
              <dt className="label-micro">Tidsplan</dt>
              <dd className="text-ink mt-0.5">{agent.scheduleLabel}</dd>
            </div>
            <div className="border-hairline bg-base/30 rounded-lg border px-2.5 py-1.5">
              <dt className="label-micro">Neste kjøring</dt>
              <dd className="text-ink mt-0.5">
                {agent.isEnabled && agent.nextRunAt ? formatDateTime(agent.nextRunAt) : '—'}
              </dd>
            </div>
          </dl>

          {agent.lastRun && (
            <p
              className={cn(
                'mt-3 rounded-lg border p-2.5 text-[13px] leading-relaxed',
                failed
                  ? 'border-danger/25 bg-danger/[0.07] text-danger'
                  : 'border-hairline bg-base/30 text-ink-soft',
              )}
            >
              <span className="label-micro mr-2">{formatRelative(agent.lastRun.startedAt)}</span>
              {failed ? agent.lastRun.error : (agent.lastRun.summary ?? 'Ingen oppsummering')}
            </p>
          )}

          {onRun && (
            <p className="text-ink-soft/70 mt-3 flex items-start gap-1.5 text-[11px] leading-snug">
              <Info size={12} className="mt-px shrink-0" aria-hidden />
              <span>{MANUAL_RUN_HINTS[agent.key]}</span>
            </p>
          )}

          <div className="mt-2 flex flex-wrap items-center gap-2">
            {onRun && (
              <Button
                size="sm"
                variant="primary"
                icon={<Play size={13} aria-hidden />}
                disabled={!agent.isEnabled || isRunning}
                loading={running}
                onClick={onRun}
              >
                Kjør nå
              </Button>
            )}
            {onToggleEnabled && (
              <Button size="sm" onClick={onToggleEnabled}>
                {agent.isEnabled ? 'Slå av' : 'Slå på'}
              </Button>
            )}
            <Button
              size="sm"
              variant="ghost"
              icon={
                <ChevronDown
                  size={13}
                  className={cn('transition-transform', expanded && 'rotate-180')}
                  aria-hidden
                />
              }
              onClick={onToggleExpand}
            >
              Innstillinger og historikk
            </Button>
          </div>

          {expanded && onToggleEnabled && <AgentSettings agent={agent} />}

          {expanded && (
            <div className="mt-3 space-y-2">
              {runs.length === 0 ? (
                <p className="text-ink-soft text-xs">Ingen kjøringer registrert.</p>
              ) : (
                runs.map((agentRun) => <RunEntry key={agentRun.id} run={agentRun} />)
              )}
            </div>
          )}
        </div>
      </div>
    </GlassPanel>
  );
}

function RunEntry({ run }: { run: AgentRunDto }) {
  const [open, setOpen] = useState(false);

  const tone =
    run.status === 'FAILED'
      ? 'text-danger'
      : run.status === 'PARTIAL'
        ? 'text-warning'
        : run.status === 'RUNNING'
          ? 'text-violet'
          : 'text-success';

  return (
    <div className="border-hairline bg-base/30 rounded-lg border">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex w-full items-start justify-between gap-3 px-3 py-2 text-left"
      >
        <span className="min-w-0">
          <span className="text-ink-soft block truncate text-[13px]">
            {run.summary ?? 'Ingen oppsummering'}
          </span>
          <span className="text-ink-soft/70 mt-0.5 block text-[11px]">
            {formatDateTime(run.startedAt)} · {AGENT_RUN_TRIGGER_LABELS[run.trigger]} ·{' '}
            {formatNumber(run.tokensIn + run.tokensOut)} tokens
            {run.isSeed && <span className="text-ink-soft/60 ml-1.5 uppercase">· demo</span>}
          </span>
        </span>
        <span className={cn('shrink-0 text-[11px]', tone)}>
          {AGENT_RUN_STATUS_LABELS[run.status]}
        </span>
      </button>

      {open && (
        <div className="border-hairline border-t p-3">
          <div className="flex items-center gap-1.5 pb-2">
            <Terminal size={12} className="text-ink-soft" aria-hidden />
            <span className="label-micro">Steg-for-steg</span>
          </div>
          <ol className="text-ink-soft space-y-1.5 font-mono text-[11px] leading-relaxed">
            {run.log.length === 0 && <li>Ingen loggsteg registrert.</li>}
            {run.log.map((entry, index) => {
              const step = entry as { at?: string; step?: string; detail?: string };
              return (
                <li key={index} className="flex gap-2">
                  <span className="text-ink-soft/50 shrink-0">
                    {step.at
                      ? new Date(step.at).toLocaleTimeString('nb-NO', { hour12: false })
                      : '—'}
                  </span>
                  <span>
                    <span className="text-accent">{step.step}</span>
                    {step.detail && <span className="text-ink-soft"> · {step.detail}</span>}
                  </span>
                </li>
              );
            })}
          </ol>
          {run.error && (
            <p className="border-danger/25 bg-danger/[0.07] text-danger mt-2 rounded border p-2 font-mono text-[11px]">
              {run.error}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
